import { Fragment, useCallback, useEffect, useReducer, useState } from 'react';

import api, {
  PresencaElement,
  PresencaElementTipoPresenca,
} from 'api/Assessor/presenca';
import ErrorHandler from 'helpers/utils/ErrorHandler';

import { Tooltip } from '@material-ui/core';

import MobileIcon from 'components/Icons/MobileIcon';
import NewAgencyIcon from 'components/Icons/NewAgencyIcon';
import { formatCPFOrCNPJ } from 'helpers/utils/cpfCnpjMask';
import moment from 'moment';
import {
  AgencyIcon,
  Col,
  EditIcon,
  IconButton,
  NavigatorWithMouse,
  Paginator,
  Row,
  ScreenSize,
  Select,
  Table,
} from 'plataforma-fundacao-componentes';
import { tabelaInitialState, tabelaReducer } from './reducer';
import useStyle from './style';

import { AdicionarConvidado } from 'components/Modal/AdicionarConvidado/AdicionarConvidado';
import { AssembleiaTipo } from 'helpers/enums/AssembleiaTipo';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import translation from 'locales/translation';
import { useDispatch } from 'react-redux';
import { closeModal, openModal } from 'store/modules/ModalActions';
import './styles.scss';

interface Props {
  idEvento: number;
  texto?: string;
  needReload?: boolean;
  onReload?: () => void;
}

export default function TabelaConvidados({
  idEvento,
  texto,
  needReload,
  onReload,
}: Props) {
  const classes = useStyle();
  const [{ rows, page, size, count }, dispatch] = useReducer(
    tabelaReducer,
    tabelaInitialState
  );

  const [totalPages, setTotalPages] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);

  const getData = useCallback(async () => {
    setLoading(true);
    const { fail, data } = await api.listConvidados(
      idEvento,
      page,
      size,
      texto
    );
    if (fail) {
      ErrorHandler(fail);
      setLoading(false);
    }
    if (data) {
      dispatch({ type: 'setRowConvidados', value: data.elements });
      dispatch({ type: 'setCountConvidados', value: data.totalElements });
      setTotalPages(data.totalPages);
      setLoading(false);
    }
  }, [idEvento, page, size, texto]);

  const getIconOfTipoPresenca = (tipoPresenca: PresencaElementTipoPresenca) => {
    switch (tipoPresenca) {
      case PresencaElementTipoPresenca.ONLINE:
        return <NavigatorWithMouse />;
      case PresencaElementTipoPresenca.OFFLINE:
        return <MobileIcon />;
      case PresencaElementTipoPresenca.AGENCIA:
        return <NewAgencyIcon />;
      case PresencaElementTipoPresenca.PRESENCIAL:
        return <AgencyIcon />;
    }
  };

  const getTipoPresenca = (row: PresencaElement, guest?: boolean) => {
    switch (row.tipoPresenca) {
      case PresencaElementTipoPresenca.ONLINE:
      case PresencaElementTipoPresenca.OFFLINE:
        return (
          <div className={classes.rowPresencaTipo}>
            {getIconOfTipoPresenca(row.tipoPresenca)}
            {translation.RelatorioPresentes.tipoPresenca[`${row.tipoPresenca}`]}
          </div>
        );
      case PresencaElementTipoPresenca.AGENCIA:
        return (
          <Tooltip
            title={`${
              translation.RelatorioPresentes.tipoAssinatura[
                `${row.tipoAssinatura}`
              ]
            }: ${row.ldapPresencaAgencia}`}
            placement={'top'}
            arrow
          >
            <div className={classes.rowPresencaTipo}>
              {getIconOfTipoPresenca(row.tipoPresenca)}
              {
                translation.RelatorioPresentes.tipoPresenca[
                  `${row.tipoPresenca}`
                ]
              }
            </div>
          </Tooltip>
        );
      case PresencaElementTipoPresenca.PRESENCIAL:
        return guest ? (
          <div className={classes.rowPresencaTipo}>
            {getIconOfTipoPresenca(row.tipoPresenca)}
            {translation.RelatorioPresentes.tipoPresenca[`${row.tipoPresenca}`]}
          </div>
        ) : (
          <Tooltip
            title={`${
              translation.RelatorioPresentes.tipoAssinatura[
                `${row.tipoAssinatura}`
              ]
            }: ${row.ldapPresencaAgencia}`}
            placement={'top'}
            arrow
          >
            <div className={classes.rowPresencaTipo}>
              {getIconOfTipoPresenca(row.tipoPresenca)}
              {
                translation.RelatorioPresentes.tipoPresenca[
                  `${row.tipoPresenca}`
                ]
              }
            </div>
          </Tooltip>
        );
    }
  };

  const dispatchAction = useDispatch();

  const abrirAdicionarConvidado = useCallback(
    (nome: string, idConvidado: string) => {
      const modalKey: string = getUniqueKey();
      dispatchAction(
        openModal(AdicionarConvidado, {
          modalKey: modalKey,
          preventEscExit: true,
          preventMaskExit: true,
          mobileOn: ScreenSize.xs,
          eventId: idEvento,
          title: translation.ConfirmarPresencaModal.title,
          nome,
          idConvidado,
          onClose: () => {
            dispatchAction(closeModal(modalKey));
          },
          onConfirm: () => {
            return new Promise<void>((resolve: any) => {
              resolve();
              dispatchAction(closeModal(modalKey));
              getData();
            });
          },
        })
      );
    },
    [dispatchAction, getData, idEvento]
  );

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (!needReload) return;
    getData();
    onReload && onReload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needReload, onReload]);

  useEffect(() => {
    dispatch({
      type: 'setPageConvidados',
      value: 1,
    });
  }, [count]);

  return (
    <Row className={'root'}>
      <Col>
        <Table
          columns={[
            {
              key: 'nomeConvidado',
              value: 'Nome do convidado',
              props: {
                align: 'left',
              },
            },
            {
              key: 'cpfcnpj',
              value: 'CPF/CNPJ',
              props: {
                align: 'left',
              },
            },
            {
              key: 'tipoPresenca',
              value: 'Tipo de presença',
              props: {
                align: 'left',
              },
            },
            {
              key: 'dataHoraPresenca',
              value: 'Data e hora presença',
              props: {
                align: 'left',
              },
            },
            {
              key: 'action',
              value: '',
              props: {
                align: 'left',
              },
            },
          ]}
          lines={rows.map(
            (
              row:
                | {
                    nome: string;
                    cpfCnpj: string | undefined;
                    tipoPresenca: AssembleiaTipo;
                    dataPresenca: string;
                  }
                | any
            ) => {
              return {
                nomeConvocado: <div>{row.nome}</div>,
                cpfcnpj: row.cpfCnpj ? formatCPFOrCNPJ(row.cpfCnpj) : '-',
                tipoPresenca: <div>{getTipoPresenca(row, true)}</div>,
                dataHoraPresenca: (
                  <div>
                    {moment(row.dataPresenca).format('DD/MM/YYYY - HH:mm')}
                  </div>
                ),
                action:
                  row.tipoPresenca === 'PRESENCIAL' ? (
                    <IconButton
                      icon={<EditIcon />}
                      onClick={() =>
                        abrirAdicionarConvidado(row.nome, row.idConvidado)
                      }
                    />
                  ) : (
                    <Fragment />
                  ),
              };
            }
          )}
        />
      </Col>
      <Col cols={[12, 12, 6, 6, 6]}>
        <Col cols={[12, 12, 6, 6, 4]} noPadding>
          <Select
            data-testid={'row-per-page'}
            label={'Linhas por página'}
            options={[
              {
                label: '10',
                value: 10,
              },
              {
                label: '25',
                value: 25,
              },
              {
                label: '50',
                value: 50,
              },
              {
                label: '100',
                value: 100,
              },
            ]}
            onChange={(e) => {
              dispatch({
                type: 'setSizeConvidados',
                value: e,
              });
              dispatch({
                type: 'setPageConvidados',
                value: 1,
              });
            }}
            placeholder={'teste'}
            value={size}
            disabled={false}
            showPanelFooter={false}
            clearButton={false}
          />
        </Col>
      </Col>
      <Col cols={[12, 12, 6, 6, 6]} end>
        {totalPages ? (
          <Paginator
            paginaAtual={page}
            quantidadeDePaginas={totalPages!}
            maxLength={7}
            loading={loading || needReload}
            onChange={(e: number) =>
              dispatch({
                type: 'setPageConvidados',
                value: e,
              })
            }
          />
        ) : undefined}
      </Col>
    </Row>
  );
}
